// src/components/TurnstileWidget.jsx
// Renders a Cloudflare Turnstile widget when REACT_APP_TURNSTILE_SITE_KEY is set.
// In development (no sitekey configured) the component renders nothing and the
// onVerify callback is never called — callers must handle the missing-token case.
import { useEffect, useRef } from 'react';

const SCRIPT_ID = 'cf-turnstile-script';
const SITEKEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

function loadTurnstileScript(onLoad) {
  if (document.getElementById(SCRIPT_ID)) {
    // Script already present — fire callback immediately if the API is ready
    if (window.turnstile) {
      onLoad();
    } else {
      document.getElementById(SCRIPT_ID).addEventListener('load', onLoad);
    }
    return;
  }
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  script.defer = true;
  script.addEventListener('load', onLoad);
  document.head.appendChild(script);
}

/**
 * @param {object} props
 * @param {(token: string) => void} props.onVerify  - called when the user passes the challenge
 * @param {() => void}              props.onExpire  - called when the token expires
 * @param {'light'|'dark'|'auto'}  [props.theme]
 */
const TurnstileWidget = ({ onVerify, onExpire, theme = 'auto' }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!SITEKEY) return; // dev environment — Turnstile disabled

    function renderWidget() {
      if (!containerRef.current || widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITEKEY,
        theme,
        callback: (token) => onVerify(token),
        'expired-callback': () => {
          if (onExpire) onExpire();
        },
        'error-callback': () => {
          if (onExpire) onExpire(); // treat errors the same as expiry
        },
      });
    }

    loadTurnstileScript(renderWidget);

    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!SITEKEY) return null;

  return <div ref={containerRef} />;
};

export default TurnstileWidget;
